import { useSearchParams } from "react-router-dom";
import type { AgentDetailResponse } from "../api/types";
import { AgentFieldValues } from "./AgentFieldValues";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { Bar, Empty } from "./States";
import { Toolbar } from "./Toolbar";
import { downloadCsv, stampedName, toCsv } from "../lib/csv";
import { EM_DASH, fmtTokens, humanise } from "../lib/format";

/** Drill-down state carried in the URL, so a field view can be linked and the
 *  browser's Back button behaves. Cleared together when leaving the field. */
const DRILL_PARAMS = ["field", "fv_page", "fv_sort", "fv_fill", "fv_q"];

/**
 * Every field this agent extracts, with how often it comes back populated and
 * how confident the model was.
 *
 * A single document's confidence says how that one extraction went. This says
 * which fields the agent can read *at all*, and that is the view that surfaces
 * a systematic gap — an agent averaging 90% while one of its fields is empty on
 * a quarter of its runs. The field list comes from the extractions themselves,
 * because each agent has its own schema.
 *
 * Each row opens the values behind its own numbers. The aggregate is where a
 * problem shows up; the values are where it is diagnosed, and the document is
 * where it is confirmed.
 */
export function AgentFields({ data }: { data: AgentDetailResponse }) {
    const fields = data.fields ?? [];
    const [params, setParams] = useSearchParams();
    const selected = params.get("field");

    const openField = (field: string) => {
        const next = new URLSearchParams(params);
        DRILL_PARAMS.forEach((k) => next.delete(k));
        next.set("field", field);
        setParams(next);
    };

    const closeField = () => {
        const next = new URLSearchParams(params);
        DRILL_PARAMS.forEach((k) => next.delete(k));
        setParams(next);
    };

    if (selected) {
        return (
            <AgentFieldValues
                documentType={data.document_type}
                field={selected}
                onBack={closeField}
            />
        );
    }

    if (!fields.length) {
        return (
            <div className="card">
                <Empty
                    title="No extractions to aggregate yet"
                    hint="This agent is registered but has not run. Its field statistics come from its own extractions, not from a fixed schema, so there is nothing to show until it does."
                />
            </div>
        );
    }

    // Called out above the table: a field never populated, or filled on fewer
    // than four runs in five, is a gap rather than noise.
    const never = fields.filter((f) => f.never_populated);
    const weak = fields.filter(
        (f) => !f.never_populated && f.fill_rate !== null && f.fill_rate < 0.8
    );

    return (
        <>
            <Toolbar
                title="Field-level Confidence"
                subtitle={`${fields.length} fields across ${data.kpis.runs} extraction${
                    data.kpis.runs === 1 ? "" : "s"
                }`}
                onExport={() =>
                    downloadCsv(
                        stampedName(`agent-fields-${data.document_type}`),
                        toCsv(fields, [
                            { header: "Field", value: (f) => f.field },
                            { header: "Runs", value: (f) => f.runs },
                            { header: "Populated", value: (f) => f.populated },
                            {
                                header: "Fill rate %",
                                value: (f) =>
                                    f.fill_rate === null ? null : (f.fill_rate * 100).toFixed(1),
                            },
                            { header: "Scored", value: (f) => f.scored },
                            { header: "Avg confidence", value: (f) => f.avg_confidence },
                            { header: "Min confidence", value: (f) => f.min_confidence },
                            { header: "Max confidence", value: (f) => f.max_confidence },
                        ])
                    )
                }
            />

            {never.length ? (
                <div className="notice notice-warn">
                    <strong>
                        {never.length} field{never.length === 1 ? " is" : "s are"} never populated
                    </strong>{" "}
                    — {never.map((f) => humanise(f.field)).join(", ")}. Empty on every one of{" "}
                    {data.kpis.runs} runs, so either these documents do not carry it or the prompt
                    cannot find it. Leaving such a field required makes every extraction retry for
                    something unobtainable.
                </div>
            ) : null}

            {weak.length ? (
                <div className="notice">
                    {weak.length} field{weak.length === 1 ? "" : "s"} populated on fewer than four
                    runs in five:{" "}
                    {weak
                        .map((f) => `${humanise(f.field)} (${Math.round((f.fill_rate ?? 0) * 100)}%)`)
                        .join(", ")}
                    .
                </div>
            ) : null}

            <div className="card">
                <div className="table-wrap">
                    <table>
                        <thead>
                            <tr>
                                <th scope="col" className="si">Sl</th>
                                <th scope="col">Field</th>
                                <th scope="col" className="num">Runs</th>
                                <th scope="col" className="num">Populated</th>
                                <th scope="col" style={{ width: 140 }}>Fill rate</th>
                                <th scope="col">Avg confidence</th>
                                <th scope="col" className="num">Range</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fields.map((f, i) => (
                                <tr
                                    key={f.field}
                                    className="row-link"
                                    onClick={() => openField(f.field)}
                                >
                                    <td className="si">{i + 1}</td>
                                    <td className="nowrap">
                                        {/* A real button, not just a clickable row:
                                            this is the row's action and it has to be
                                            reachable from the keyboard. */}
                                        <button
                                            className="link-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openField(f.field);
                                            }}
                                            title={`Show every value read for ${humanise(f.field)}`}
                                        >
                                            {humanise(f.field)}
                                        </button>
                                        {/* Marked here as well as in the Scored
                                            fields card above, so the two tables
                                            on this tab cannot tell different
                                            stories about which fields count. */}
                                        {f.required ? (
                                            <>
                                                {" "}
                                                <span
                                                    className="pill pill-warning"
                                                    title="This agent is scored on this field"
                                                >
                                                    scored
                                                </span>
                                            </>
                                        ) : null}
                                        {f.never_populated ? (
                                            <>
                                                {" "}
                                                <span className="pill pill-critical">always empty</span>
                                            </>
                                        ) : null}
                                    </td>
                                    <td className="num">{fmtTokens(f.runs)}</td>
                                    <td className="num">{fmtTokens(f.populated)}</td>
                                    <td>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <Bar value={f.populated} max={f.runs} />
                                            <span
                                                className="muted"
                                                style={{ fontSize: 12, minWidth: 34, textAlign: "right" }}
                                            >
                                                {f.fill_rate === null
                                                    ? EM_DASH
                                                    : `${Math.round(f.fill_rate * 100)}%`}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <ConfidenceBadge
                                            value={f.avg_confidence}
                                            absentReason="No extraction of this field carried a confidence score"
                                        />
                                        {/* Said plainly: an average over 3 of 25 runs is not
                                            the same claim as one over all 25. */}
                                        {f.scored < f.runs ? (
                                            <div className="muted" style={{ fontSize: 12 }}>
                                                {f.scored} of {f.runs} scored
                                            </div>
                                        ) : null}
                                    </td>
                                    <td className="num muted">
                                        {f.min_confidence === null || f.max_confidence === null
                                            ? EM_DASH
                                            : `${f.min_confidence}–${f.max_confidence}`}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="notice" style={{ marginBottom: 0 }}>
                <strong>Click any field</strong> to see every value the agent has read for it,
                with the confidence on each and a link to the original document. A populated
                field is not necessarily a correct one — a model answering &ldquo;Unknown&rdquo;
                counts as populated here. Fill rate measures whether the agent returns something,
                and confidence is its own self-report. Neither is accuracy, which would need
                labelled ground truth this platform does not collect.
            </div>
        </>
    );
}
